import { useState, type FormEvent } from "react";
import { Download, Plus, Trash2 } from "lucide-react";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { api } from "@/lib/api";
import {
  containsPatientIdentifier,
  todayIsoDate,
  useBoardCpd,
  useCreateCpdEntry,
  useDeleteCpdEntry,
  type CpdActivityType,
} from "@/lib/board";

const ACTIVITY_LABELS: Record<CpdActivityType, string> = {
  educational: "Educational activities",
  reviewing_performance: "Reviewing performance",
  measuring_outcomes: "Measuring outcomes",
};

export default function BoardCpd() {
  const { city } = useCity();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [activityType, setActivityType] = useState<CpdActivityType>("educational");
  const [minutes, setMinutes] = useState(30);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [loggedOn, setLoggedOn] = useState(todayIsoDate());
  const [formError, setFormError] = useState<string | null>(null);
  const [exportError, setExportError] = useState(false);
  const query = useBoardCpd(city.slug, year);
  const create = useCreateCpdEntry(city.slug);
  const remove = useDeleteCpdEntry(city.slug);
  const years = Array.from(new Set([year, ...Array.from({ length: 5 }, (_, index) => currentYear - index)])).sort((a, b) => b - a);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (containsPatientIdentifier(title) || containsPatientIdentifier(note)) {
      setFormError("Remove patient identifiers, Medicare numbers and patient-linked dates before saving.");
      return;
    }
    if (!title.trim() || !Number.isInteger(minutes) || minutes < 1 || minutes > 480 || !loggedOn) {
      setFormError("Add a title, date and a whole number of minutes between 1 and 480.");
      return;
    }
    setFormError(null);
    create.mutate(
      {
        tileId: null,
        activityType,
        minutes,
        title: title.trim(),
        note: note.trim(),
        loggedOn,
      },
      {
        onSuccess: () => {
          track("cpd_logged", { activity_type: activityType, minutes });
          setTitle("");
          setNote("");
          setMinutes(30);
          setYear(Number(loggedOn.slice(0, 4)));
        },
      },
    );
  };

  const exportCsv = async () => {
    setExportError(false);
    try {
      const csv = await api.getText(`/${city.slug}/v1/board/cpd.csv`, { params: { year } });
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const download = document.createElement("a");
      download.href = url;
      download.download = `pulseboard-cpd-${year}.csv`;
      document.body.appendChild(download);
      download.click();
      download.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(true);
    }
  };

  return (
    <div>
      <header className="pb-board-page-heading pb-board-page-heading--with-control">
        <div>
          <p className="pb-board-eyebrow">Your private activity record</p>
          <h1>CPD log</h1>
          <p>Track minutes by activity type and export a clean annual record.</p>
        </div>
        <div className="pb-board-heading-controls">
          <label>Year<select value={year} onChange={(event) => setYear(Number(event.target.value))}>{years.map((item) => <option key={item}>{item}</option>)}</select></label>
          <button type="button" onClick={() => void exportCsv()}><Download size={15} aria-hidden="true" />Export CSV</button>
        </div>
      </header>

      {exportError ? <p className="pb-board-form-error" role="alert">The CSV could not be exported. Try again.</p> : null}

      {query.data ? (
        <section className="pb-board-cpd-totals" aria-label={`${year} CPD totals`}>
          <div><strong>{formatHours(query.data.totals.hours)}</strong><span>Total hours</span><small>{query.data.totals.minutes} minutes</small></div>
          <div><strong>{query.data.totals.educational}</strong><span>Educational</span><small>minutes</small></div>
          <div><strong>{query.data.totals.reviewing_performance}</strong><span>Reviewing performance</span><small>minutes</small></div>
          <div><strong>{query.data.totals.measuring_outcomes}</strong><span>Measuring outcomes</span><small>minutes</small></div>
        </section>
      ) : null}

      <div className="pb-board-cpd-layout">
        <section className="pb-board-card pb-board-cpd-form" aria-labelledby="add-cpd-heading">
          <h2 id="add-cpd-heading"><Plus size={16} aria-hidden="true" />Add activity</h2>
          <form onSubmit={submit}>
            <label>Activity type<select value={activityType} onChange={(event) => setActivityType(event.target.value as CpdActivityType)}>{Object.entries(ACTIVITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <div className="pb-board-form-row">
              <label>Minutes<input required type="number" min={1} max={480} value={minutes} onChange={(event) => setMinutes(Number(event.target.value))} /></label>
              <label>Date<input required type="date" min="2000-01-01" max="2100-12-31" value={loggedOn} onChange={(event) => setLoggedOn(event.target.value)} /></label>
            </div>
            <label>Title<input required maxLength={140} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Activity title" /></label>
            <label>Note <small>{note.length}/200</small><textarea maxLength={200} rows={4} value={note} onChange={(event) => { setNote(event.target.value); setFormError(null); }} placeholder="Optional. Do not include patient identifiers." /></label>
            <button className="pb-board-primary" type="submit" disabled={create.isPending || !title.trim() || minutes < 1 || minutes > 480}>{create.isPending ? "Saving…" : "Add to CPD log"}</button>
            {formError ? <p className="pb-board-form-error" role="alert">{formError}</p> : null}
            {create.isError ? <p className="pb-board-form-error" role="alert">The activity could not be saved. Check the note contains no patient identifiers.</p> : null}
          </form>
        </section>

        <section className="pb-board-card pb-board-cpd-table-card" aria-labelledby="cpd-entries-heading">
          <h2 id="cpd-entries-heading">{year} activities</h2>
          {query.isLoading ? <div className="pb-board-state">Loading CPD entries…</div> : null}
          {query.isError ? <div className="pb-board-state pb-board-state--error" role="alert">Your CPD log could not be loaded. <button type="button" onClick={() => void query.refetch()}>Retry</button></div> : null}
          {query.data?.entries.length === 0 ? <p className="pb-board-empty">No CPD entries have been logged for {year}.</p> : null}
          {query.data?.entries.length ? (
            <div className="pb-board-table-wrap">
              <table className="pb-board-table">
                <thead><tr><th>Date</th><th>Activity</th><th>Type</th><th>Minutes</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {query.data.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.loggedOn)}</td>
                      <td><strong>{entry.title}</strong>{entry.note ? <small>{entry.note}</small> : null}</td>
                      <td>{ACTIVITY_LABELS[entry.activityType]}</td>
                      <td>{entry.minutes}</td>
                      <td><button type="button" className="pb-board-icon-button" disabled={remove.isPending} aria-label={`Delete ${entry.title}`} onClick={() => { if (window.confirm("Delete this CPD entry?")) remove.mutate(entry.id); }}><Trash2 size={15} aria-hidden="true" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function formatHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatDate(value: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return value;
  return `${parts[3]}/${parts[2]}/${parts[1]}`;
}
